import { useSyncExternalStore } from 'react';
import { isDemoFeatureEnvironment } from '@/lib/hub-environment';

export const MAX_PRESENTATION_CLIENTS = 4;

export const DEFAULT_PRESENTATION_CLIENT_NAMES = ['Northwind Energy', 'Atlas Telecom', 'Harbour Finance', 'Meridian Utilities'];

export interface PresentationModeState {
  /** Never true on a build that is not local or staging, whatever local storage holds. */
  enabled: boolean;
  /** 1 to MAX_PRESENTATION_CLIENTS trimmed, non-empty names. */
  clientNames: string[];
  /** Offer the quick toggle in the Analytics app sidebar as well as the Hub sidebar. */
  showInAppSidebar: boolean;
}

const STORAGE_KEY = 'hub.presentationMode';

const DEFAULT_STATE: PresentationModeState = { enabled: false, clientNames: DEFAULT_PRESENTATION_CLIENT_NAMES, showInAppSidebar: true };

/** Trim, drop blanks, cap at MAX_PRESENTATION_CLIENTS; an empty result falls back to the defaults. */
export function normalizeClientNames(names: readonly string[]): string[] {
  const cleaned = names.map((name) => name.trim()).filter((name) => name.length > 0);
  if (cleaned.length === 0) return DEFAULT_PRESENTATION_CLIENT_NAMES;
  return cleaned.slice(0, MAX_PRESENTATION_CLIENTS);
}

function parse(raw: string | null): PresentationModeState {
  if (!raw) return DEFAULT_STATE;
  try {
    const parsed = JSON.parse(raw) as Partial<PresentationModeState>;
    const names = Array.isArray(parsed.clientNames) ? parsed.clientNames.filter((n): n is string => typeof n === 'string') : [];
    return {
      enabled: parsed.enabled === true && isDemoFeatureEnvironment(),
      clientNames: normalizeClientNames(names),
      showInAppSidebar: parsed.showInAppSidebar !== false,
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function read(): PresentationModeState {
  try {
    return parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_STATE;
  }
}

let snapshot: PresentationModeState = read();
const listeners = new Set<() => void>();

function write(next: PresentationModeState): void {
  snapshot = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable (private window, quota): the in-memory snapshot still drives this session.
  }
  for (const listener of listeners) listener();
}

export function getPresentationModeState(): PresentationModeState {
  return snapshot;
}

/** Non-React read for services that must know whether to serve fixtures. */
export function isPresentationModeEnabled(): boolean {
  return snapshot.enabled;
}

export function setPresentationModeEnabled(enabled: boolean): void {
  write({ ...snapshot, enabled: enabled && isDemoFeatureEnvironment() });
}

export function setPresentationShowInAppSidebar(showInAppSidebar: boolean): void {
  write({ ...snapshot, showInAppSidebar });
}

export function setPresentationClientNames(names: readonly string[]): void {
  write({ ...snapshot, clientNames: normalizeClientNames(names) });
}

export function subscribePresentationMode(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePresentationMode(): PresentationModeState {
  return useSyncExternalStore(subscribePresentationMode, getPresentationModeState, getPresentationModeState);
}

/** While Demo mode is on, the Hub presents as the first demo client: its name labels the context switcher and the tab title. */
export function useDemoContextLabel(): string | null {
  const { enabled, clientNames } = usePresentationMode();
  return enabled ? (clientNames[0] ?? null) : null;
}
